import { api } from '@/lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compress an image file client-side using canvas.
 * Returns a Blob (image/jpeg or image/png).
 */
const compressImage = (file, { maxDim = 1600, quality = 0.82 } = {}) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_failed'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => {
        // unsupported (HEIC etc.) -> send original; backend will try Pillow
        resolve(file);
      };
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((maxDim / width) * height);
            width = maxDim;
          } else {
            width = Math.round((maxDim / height) * width);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const isPng = file.type === 'image/png';
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const ext = isPng ? 'png' : 'jpg';
            const out = new File([blob], (file.name || 'upload').replace(/\.[^.]+$/, '') + '.' + ext, {
              type: isPng ? 'image/png' : 'image/jpeg',
            });
            resolve(out);
          },
          isPng ? 'image/png' : 'image/jpeg',
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

/**
 * Upload an image (compressing first), return absolute URL.
 * Shows toasts on progress / failure.
 */
export const uploadImage = async (file, opts = {}) => {
  if (!file) return null;
  const sizeMb = (file.size / 1024 / 1024).toFixed(1);
  const toastId = toast.loading(`Uploading (${sizeMb}MB)…`);
  try {
    const compressed = await compressImage(file, opts);
    const fd = new FormData();
    fd.append('file', compressed);
    fd.append('compress', 'true');
    if (opts.targetKb && opts.targetKb > 0) {
      fd.append('target_kb', String(opts.targetKb));
    }
    // IMPORTANT: do NOT manually set Content-Type; axios will set the multipart boundary.
    const { data } = await api.post('/upload', fd, {
      timeout: 120000,
      onUploadProgress: (e) => {
        if (e.total) {
          const pct = Math.round((e.loaded / e.total) * 100);
          toast.loading(`Uploading… ${pct}%`, { id: toastId });
        }
      },
    });
    const url = `${BACKEND_URL}${data.url}`;
    const kb = Math.round(data.size / 1024);
    toast.success(`Uploaded · ${kb} KB`, { id: toastId });
    return { url, file_id: data.file_id, size: data.size };
  } catch (err) {
    console.error('upload failed', err);
    const msg = err.response?.data?.detail || err.message || 'Upload failed';
    toast.error(`Upload failed: ${msg}`, { id: toastId });
    return null;
  }
};
